// 角色映射語音服務 - 專門處理模擬考試頁面的語音
import { TTSService } from './ttsService'
import { UNIVERSAL_ROLE_VOICE_MAP } from './speech'

// 遞歸查找角色的公共函數
function findRoleInfo(role: string): { 
  voice: 'male' | 'female', 
  voice_id?: number,
  browserVoiceKeywords?: string[]
} {
  // 精確匹配角色
  if (UNIVERSAL_ROLE_VOICE_MAP[role]) {
    // console.log(`✅ 精確匹配角色: "${role}"`)
    return {
      voice: UNIVERSAL_ROLE_VOICE_MAP[role].voice,
      voice_id: UNIVERSAL_ROLE_VOICE_MAP[role].voice_id || UNIVERSAL_ROLE_VOICE_MAP[role].voicevoxvoice_id,
      browserVoiceKeywords: UNIVERSAL_ROLE_VOICE_MAP[role].browserVoiceKeywords
    }
  }

  // 嘗試部分匹配
  const partialMatches = Object.keys(UNIVERSAL_ROLE_VOICE_MAP).filter(
    key => role.includes(key)
  )

  if (partialMatches.length > 0) {
    const matchedRole = partialMatches[0]
    // console.log(`🔎 部分匹配角色: "${role}" → "${matchedRole}"`)
    return {
      voice: UNIVERSAL_ROLE_VOICE_MAP[matchedRole].voice,
      voice_id: UNIVERSAL_ROLE_VOICE_MAP[matchedRole].voice_id || UNIVERSAL_ROLE_VOICE_MAP[matchedRole].voicevoxvoice_id,
      browserVoiceKeywords: UNIVERSAL_ROLE_VOICE_MAP[matchedRole].browserVoiceKeywords
    }
  }
  
  // 最終備用
  console.warn(`⚠️ 未找到角色映射，使用備用角色: "${role}"`)
  return UNIVERSAL_ROLE_VOICE_MAP['備用']
}

// 解析對話，忽略角色名稱，支持多級備用
function parseDialogue(script: string): { 
  dialogueParts: Array<{
    role: string, 
    text: string,
    voice: 'male' | 'female', 
    voice_id?: number,
    browserVoiceKeywords?: string[]
  }>, 
  characters: Array<{
    role: string, 
    voice: 'male' | 'female', 
    voice_id?: number,
    browserVoiceKeywords?: string[]
  }>
} {
  console.group('🔍 對話解析詳情')
  // console.log('原始腳本:', script)

  // 更嚴格的角色名稱解析正則表達式
  const dialogueParts = script.split('\n')
    .map(part => {
      // 更複雜的正則表達式，處理更多角色名稱格式
      const roleRegex = /^([^:：]+)[:：]\s*(.+)$/
      const match = part.match(roleRegex)
      
      if (match) {
        const [, roleName, dialogue] = match
        // console.log(`🏷️ 檢測到角色: "${roleName}"`)
        // console.log(`💬 原始對話: "${part}"`)
        // console.log(`🗣️ 提取對話: "${dialogue.trim()}"`)
        return { 
          role: roleName.trim(), 
          text: dialogue.trim() 
        }
      }
      
      return { 
        role: '備用', 
        text: part.trim() 
      }
    })
    .filter(part => part.text) // 移除空白對話

  // console.log('解析後對話部分:', dialogueParts)

  // 為每個對話部分找到對應的語音配置
  const characters = dialogueParts.map(part => {
    const roleInfo = findRoleInfo(part.role)
    
    return {
      role: part.role,
      ...roleInfo
    }
  })

  // console.log('最終角色映射:', characters)
  console.groupEnd()

  return { 
    dialogueParts: dialogueParts.map(part => ({
      role: part.role,
      text: part.text,
      ...findRoleInfo(part.role)
    })), 
    characters 
  }
}

export class RoleBasedTTSService implements TTSService {
  private baseTTSService: TTSService

  constructor(baseTTSService: TTSService) {
    this.baseTTSService = baseTTSService
  }

  async speak(text: string, options: {
    voice?: 'male' | 'female',
    language?: string,
    pitch?: number,
    rate?: number,
    voice_id?: number,
    fallbackToBrowser?: boolean,
    role?: string,
    userSessionId?: string,  // 新增：用戶會話 ID
    skipDialogueParsing?: boolean  // 新增：跳過對話解析標記
  } = {}): Promise<void> {
    try {
      // console.group('🎭 角色映射語音服務')
      // console.log('輸入文本:', text)
      // console.log('輸入選項:', options)

      // 如果提供了角色，使用角色映射
      if (options.role) {
        // console.log(`🎭 使用角色映射: "${options.role}"`)
        
        const roleInfo = findRoleInfo(options.role)
        // console.log('🔍 找到角色映射:', roleInfo)
        
        if (roleInfo.voice_id) {
          // console.log(`🎤 根據角色 ${options.role} 選擇語音 ID: ${roleInfo.voice_id}`)
          
          // 檢查是否為對話格式，如果是則解析後逐段播放
          if (!options.skipDialogueParsing && (text.includes(':') || text.includes('：'))) {
            // console.log('🎭 檢測到對話格式，解析後逐段播放')
            const { dialogueParts } = parseDialogue(text)
            
            for (let i = 0; i < dialogueParts.length; i++) {
              const part = dialogueParts[i]
              // console.log(`🔊 播放對話部分 - 角色: ${part.role}, 文本: ${part.text}`)
              
              if (part.voice_id) {
                await this.baseTTSService.speak(part.text, {
                  voice: part.voice,
                  voice_id: part.voice_id,
                  language: 'ja-JP',
                  role: part.role,
                  fallbackToBrowser: true,
                  userSessionId: options.userSessionId
                })
              } else {
                // 如果找不到角色映射，使用提供的角色映射
                await this.baseTTSService.speak(part.text, {
                  voice: roleInfo.voice,
                  voice_id: roleInfo.voice_id,
                  language: 'ja-JP',
                  role: part.role,
                  fallbackToBrowser: true,
                  userSessionId: options.userSessionId
                })
              }
              
              // 只在不是最後一句時添加停頓（減少到 100ms）
              if (i < dialogueParts.length - 1) {
                await new Promise(resolve => setTimeout(resolve, 100))
              }
            }
          } else {
            // 單一文本，直接播放
            await this.baseTTSService.speak(text, {
              voice: roleInfo.voice,
              voice_id: roleInfo.voice_id,
              language: 'ja-JP',
              role: options.role,
              fallbackToBrowser: true,
              userSessionId: options.userSessionId
            })
          }
          
          // console.log('🎭 角色映射語音播放完成')
          console.groupEnd()
          return
        }
      }

      // 如果沒有角色或找不到映射，嘗試解析對話
      if (!options.skipDialogueParsing && (text.includes(':') || text.includes('：'))) {
        // console.log('🎭 檢測到對話格式，嘗試解析角色')
        
        const { dialogueParts } = parseDialogue(text)
        
        if (dialogueParts.length > 0) {
          // console.log('解析後的對話部分:', dialogueParts)
          
          // 遍歷對話部分
          for (const part of dialogueParts) {
            // console.log(`🔊 當前播放部分 - 角色: ${part.role}, 文本: ${part.text}`)

            if (part.voice_id) {
              // 使用基礎語音服務播放
              await this.baseTTSService.speak(part.text, {
                voice: part.voice,
                voice_id: part.voice_id,
                language: 'ja-JP',
                role: part.role,
                fallbackToBrowser: true,
                userSessionId: options.userSessionId
              })
            } else {
              // 如果沒有 voice_id，使用預設語音
              await this.baseTTSService.speak(part.text, {
                voice: part.voice,
                language: 'ja-JP',
                role: part.role,
                fallbackToBrowser: true,
                userSessionId: options.userSessionId
              })
            }

            // 每句話之間增加短暫停頓
            await new Promise(resolve => setTimeout(resolve, 500))
          }
          
          // console.log('🎭 對話解析語音播放完成')
          console.groupEnd()
          return
        }
      }

      // 如果都沒有，使用預設語音
      // console.log('🎭 使用預設語音播放')
      await this.baseTTSService.speak(text, {
        voice: options.voice || 'female',
        language: 'ja-JP',
        fallbackToBrowser: true,
        userSessionId: options.userSessionId
      })
      
      // console.log('🎭 預設語音播放完成')
      console.groupEnd()
      
    } catch (error) {
      console.error('🚨 RoleBasedTTSService 執行失敗:', error)
      console.groupEnd()
      throw error
    }
  }

  async getAvailableVoices(): Promise<Array<{
    id: string,
    name: string,
    language: string,
    gender: 'male' | 'female'
  }>> {
    return this.baseTTSService.getAvailableVoices()
  }

  async isServiceAvailable?(): Promise<{
    available: boolean,
    message?: string,
    details?: any
  }> {
    return this.baseTTSService.isServiceAvailable?.() || { available: true }
  }
}
